/**
 * Pearl Color Visual Component
 * Creates animated liquid waves or rotating orbs based on pearl color descriptions
 */

class PearlColorVisual {
  constructor(container) {
    // Check if already has content rendered
    const hasContent = container.querySelector('.pearl-visual--liquid, .pearl-visual--orbs');
    if (hasContent && container.dataset.pearlInitialized === 'true') {
      console.log('[Pearl Visual] Already initialized with content, skipping...');
      return;
    }
    
    this.container = container;
    this.description = container.dataset.description || '';
    this.style = container.dataset.style || 'liquid';
    const extractedColors = container.dataset.extractedColors || '';
    
    // ALWAYS parse description for badge color names
    const descriptionColors = this.parseColors(this.description);
    
    // Try to use AI-extracted hex codes for gradient, fallback to description
    if (extractedColors && extractedColors !== 'null' && extractedColors !== '') {
      try {
        const parsed = JSON.parse(extractedColors);
        if (Array.isArray(parsed) && parsed.length > 0) {
          console.log('[Pearl Visual] Using AI-extracted hex codes for gradient:', parsed);
          console.log('[Pearl Visual] Using description names for badges:', descriptionColors.map(c => c.name));
          
          // Use AI hex codes for gradient, description names for badges
          this.colors = parsed.map((hex, i) => ({
            name: hex, // Use hex for gradient (will be mapped in render)
            hex: hex
          }));
          
          // Store description colors separately for badges
          this.badgeColors = descriptionColors;
        } else {
          console.log('[Pearl Visual] Invalid extracted colors, using description...');
          this.colors = descriptionColors;
          this.badgeColors = descriptionColors;
        }
      } catch (e) {
        console.log('[Pearl Visual] Error parsing extracted colors, using description:', e);
        this.colors = descriptionColors;
        this.badgeColors = descriptionColors;
      }
    } else {
      console.log('[Pearl Visual] No extracted colors, using description:', this.description);
      this.colors = descriptionColors;
      this.badgeColors = descriptionColors;
    }
    
    console.log('[Pearl Visual] Final gradient colors:', this.colors);
    console.log('[Pearl Visual] Final badge colors:', this.badgeColors);
    
    if (this.colors.length === 0) {
      console.warn('[Pearl Visual] No colors extracted, using defaults');
    }
    
    this.init();
    container.dataset.pearlInitialized = 'true';
  }

  // Color keyword to hex mapping
  colorMap = {
    // Primary colors
    'blue': '#4361ee',
    'purple': '#7209b7',
    'pink': '#f72585',
    'red': '#d00000',
    'orange': '#ff6d00',
    'yellow': '#ffea00',
    'green': '#2d6a4f',
    'white': '#f8f9fa',
    'black': '#212529',
    
    // Extended colors
    'copper': '#b7410e',
    'gold': '#ffd60a',
    'teal': '#008080',
    'cyan': '#00b4d8',
    'magenta': '#d90368',
    'violet': '#8338ec',
    'turquoise': '#06ffa5',
    'rose': '#ff006e',
    'amber': '#ffb703',
    'maroon': '#800020',
    'fuchsia': '#ff00ff',
    'silver': '#adb5bd',
    'grey': '#6c757d',
    'gray': '#6c757d',
    'indigo': '#4f46e5',
    
    // Descriptive terms
    'navy': '#1e3a8a',
    'royal': '#1d4ed8',
    'sky': '#0ea5e9',
    'lime': '#84cc16',
    'olive': '#6b7208',
    'mint': '#6ee7b7',
    'salmon': '#fb7185',
    'coral': '#ff7f50',
    'crimson': '#dc143c',
    'scarlet': '#ff2400',
    'ruby': '#e0115f',
    'emerald': '#10b981',
    'sapphire': '#0f52ba',
    'lavender': '#c4b5fd',
    'plum': '#9333ea',
    'burgundy': '#800020',
    'bronze': '#cd7f32',
    'brass': '#b5a642',
    'champagne': '#f7e7ce',
    'cream': '#fffdd0',
    'ivory': '#fffff0',
    'pearl': '#eae0c8',
    'graphite': '#383838',
    'charcoal': '#36454f',
    'slate': '#708090',
    'steel': '#71797e',
    'titanium': '#878681',
  };

  parseColors(description) {
    if (!description) return [];
    
    const colors = [];
    const lowerDesc = description.toLowerCase();
    
    // Common words to filter out
    const stopWords = ['to', 'and', 'or', 'the', 'a', 'an', 'with', 'in', 'on', 'at', 'from', 'of'];
    
    // Try to extract colors from common patterns
    // Pattern 1: "Shifts from X/Y/Z" or "Shifts between X, Y, Z"
    const shiftPattern = /shifts?\s+(?:from|between)\s+([\w\s,/&]+?)(?:\.|$|with)/i;
    const match = description.match(shiftPattern);
    
    if (match) {
      // Extract individual color words
      const colorString = match[1];
      const colorWords = colorString.split(/[,/&\s]+/).filter(w => w.length > 2);
      
      colorWords.forEach(word => {
        let normalizedWord = word.toLowerCase().trim();
        
        // Skip stop words
        if (stopWords.includes(normalizedWord)) return;
        
        // Try to match singular form (strip trailing 's' for plurals)
        if (!this.colorMap[normalizedWord] && normalizedWord.endsWith('s') && normalizedWord.length > 3) {
          const singular = normalizedWord.slice(0, -1);
          if (this.colorMap[singular]) {
            normalizedWord = singular;
          }
        }
        
        if (this.colorMap[normalizedWord]) {
          // Capitalize first letter of original word
          const displayName = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          colors.push({
            name: displayName,
            hex: this.colorMap[normalizedWord]
          });
        }
      });
    }
    
    // Fallback: scan for any color keywords in description (including plurals)
    if (colors.length === 0) {
      Object.keys(this.colorMap).forEach(colorName => {
        // Match both singular and plural forms
        const regex = new RegExp(`\\b${colorName}s?\\b`, 'i');
        if (regex.test(lowerDesc)) {
          colors.push({
            name: colorName.charAt(0).toUpperCase() + colorName.slice(1),
            hex: this.colorMap[colorName]
          });
        }
      });
    }
    
    // Remove duplicates
    const uniqueColors = [];
    const seenHex = new Set();
    colors.forEach(color => {
      if (!seenHex.has(color.hex)) {
        seenHex.add(color.hex);
        uniqueColors.push(color);
      }
    });
    
    // Ensure at least 2 colors for visual effect
    if (uniqueColors.length === 0) {
      // Mark as defaults so we can hide badges
      uniqueColors.push(
        { name: 'Pearl', hex: '#eae0c8', isDefault: true },
        { name: 'Silver', hex: '#adb5bd', isDefault: true }
      );
    } else if (uniqueColors.length === 1) {
      // Add a complementary color
      uniqueColors.push({ name: 'Shimmer', hex: this.adjustBrightness(uniqueColors[0].hex, 40) });
    }
    
    return uniqueColors;
  }

  adjustBrightness(hex, percent) {
    // Convert hex to RGB
    const num = parseInt(hex.replace('#', ''), 16);
    const r = (num >> 16) + percent;
    const g = ((num >> 8) & 0x00FF) + percent;
    const b = (num & 0x0000FF) + percent;
    
    // Ensure values are within 0-255
    const newR = Math.max(0, Math.min(255, r));
    const newG = Math.max(0, Math.min(255, g));
    const newB = Math.max(0, Math.min(255, b));
    
    return `#${((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, '0')}`;
  }

  init() {
    // Clear any existing content
    this.container.innerHTML = '';
    console.log('[Pearl Visual] Container cleared, innerHTML:', this.container.innerHTML);
    
    if (this.style === 'liquid') {
      console.log('[Pearl Visual] Rendering liquid waves...');
      this.renderLiquidWaves();
    } else if (this.style === 'orbs') {
      console.log('[Pearl Visual] Rendering rotating orbs...');
      this.renderRotatingOrbs();
    }
    
    // Verify DOM after render
    setTimeout(() => {
      console.log('[Pearl Visual] DOM check - container children:', this.container.children.length);
      console.log('[Pearl Visual] Container innerHTML length:', this.container.innerHTML.length);
      console.log('[Pearl Visual] First 500 chars:', this.container.innerHTML.substring(0, 500));
    }, 100);
  }

  renderLiquidWaves() {
    console.log(`[Pearl Visual] Creating paint gradient for ${this.colors.length} colors...`);
    
    // Create smooth diagonal gradient with all colors evenly distributed
    // Use this.colors (AI hex codes) for gradient
    const colorStops = [];
    this.colors.forEach((c, i) => {
      const position = (i / (this.colors.length - 1)) * 100;
      colorStops.push(`${c.hex} ${position}%`);
      console.log(`[Pearl Visual] Gradient color ${i}: ${c.hex} at ${position.toFixed(1)}%`);
    });
    
    const gradient = `linear-gradient(135deg, ${colorStops.join(', ')})`;
    
    const animations = `
      <style>
        @keyframes pearlShift {
          0%, 100% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
        }
      </style>
    `;
    
    // Apply gradient directly to container background
    this.container.style.background = gradient;
    this.container.style.backgroundSize = '200% 200%';
    this.container.style.animation = 'pearlShift 8s ease-in-out infinite';
    
    // Use this.badgeColors (description names) for badges
    const badgeColorsToShow = this.badgeColors || this.colors;
    
    // Don't show badges if they're just defaults (Pearl/Silver with no real color words)
    const hasDefaultsOnly = badgeColorsToShow.length === 2 && 
      badgeColorsToShow.every(c => c.isDefault === true);
    
    console.log(`[Pearl Visual] Rendering ${hasDefaultsOnly ? 0 : badgeColorsToShow.length} badge(s) with names`);
    
    // Add color labels overlay - no wrapping (hide if defaults only)
    const labelsHTML = hasDefaultsOnly ? '' : `
      <div style="
        position: absolute;
        bottom: 15px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
        justify-content: center;
        max-width: 95%;
        overflow-x: auto;
      ">
        ${badgeColorsToShow.map(c => `
          <div style="
            background: rgba(0,0,0,0.75);
            padding: 5px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            color: white;
            border: 2px solid ${c.hex};
            white-space: nowrap;
            flex-shrink: 0;
          ">
            ${c.name}
          </div>
        `).join('')}
      </div>
    `;
    
    this.container.innerHTML = animations + labelsHTML;
    console.log('[Pearl Visual] ✅ Paint gradient with description-based badges');
  }

  renderRotatingOrbs() {
    console.log(`[Pearl Visual] Creating radial burst for ${this.colors.length} colors...`);
    
    // Create radial color stops from center outward
    // Use this.colors (AI hex codes) for gradient
    const colorStops = [];
    this.colors.forEach((c, i) => {
      const position = (i / (this.colors.length - 1)) * 100;
      colorStops.push(`${c.hex} ${position}%`);
      console.log(`[Pearl Visual] Gradient color ${i}: ${c.hex} at ${position.toFixed(1)}%`);
    });
    
    const gradient = `radial-gradient(circle at center, ${colorStops.join(', ')})`;
    
    const animations = `
      <style>
        @keyframes radialBurst {
          0%, 100% { background-size: 100% 100%; background-position: center; }
          50% { background-size: 150% 150%; background-position: center; }
        }
      </style>
    `;
    
    // Apply gradient directly to container
    this.container.style.background = gradient;
    this.container.style.backgroundSize = '100% 100%';
    this.container.style.backgroundPosition = 'center';
    this.container.style.animation = 'radialBurst 6s ease-in-out infinite';
    
    // Use this.badgeColors (description names) for badges
    const badgeColorsToShow = this.badgeColors || this.colors;
    
    // Don't show badges if they're just defaults (Pearl/Silver with no real color words)
    const hasDefaultsOnly = badgeColorsToShow.length === 2 && 
      badgeColorsToShow.every(c => c.isDefault === true);
    
    console.log(`[Pearl Visual] Rendering ${hasDefaultsOnly ? 0 : badgeColorsToShow.length} badge(s) with names`);
    
    // Add color labels - no wrapping (hide if defaults only)
    const labelsHTML = hasDefaultsOnly ? '' : `
      <div style="
        position: absolute;
        bottom: 15px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
        justify-content: center;
        max-width: 95%;
        overflow-x: auto;
      ">
        ${badgeColorsToShow.map(c => `
          <div style="
            background: rgba(0,0,0,0.75);
            padding: 5px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            color: white;
            border: 2px solid ${c.hex};
            white-space: nowrap;
            flex-shrink: 0;
          ">
            ${c.name}
          </div>
        `).join('')}
      </div>
    `;
    
    this.container.innerHTML = animations + labelsHTML;
    console.log('[Pearl Visual] ✅ Radial burst with description-based badges');
  }

  // Optional: Render color labels
  renderColorLabels(parentElement) {
    if (this.colors.length === 0) return;
    
    const labelsContainer = document.createElement('div');
    labelsContainer.className = 'pearl-visual__color-labels';
    
    this.colors.forEach(color => {
      const label = document.createElement('span');
      label.className = 'pearl-visual__color-label';
      
      const dot = document.createElement('span');
      dot.className = 'pearl-visual__color-dot';
      dot.style.backgroundColor = color.hex;
      
      const name = document.createElement('span');
      name.textContent = color.name;
      
      label.appendChild(dot);
      label.appendChild(name);
      labelsContainer.appendChild(label);
    });
    
    parentElement.appendChild(labelsContainer);
  }
}

// Initialize all pearl color visuals on page load
function initPearlColorVisuals() {
  const containers = document.querySelectorAll('.pearl-visual__container');
  console.log(`[Pearl Visual] Found ${containers.length} containers to initialize`);
  
  containers.forEach((container, index) => {
    const description = container.dataset.description;
    const style = container.dataset.style;
    console.log(`[Pearl Visual ${index}] Description: "${description}", Style: ${style}`);
    
    try {
      new PearlColorVisual(container);
      console.log(`[Pearl Visual ${index}] ✅ Initialized successfully`);
    } catch (error) {
      console.error(`[Pearl Visual ${index}] ❌ Error:`, error);
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPearlColorVisuals);
} else {
  initPearlColorVisuals();
}

// Re-initialize on theme editor updates
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function() {
    console.log('[Pearl Visual] Theme editor section loaded, re-initializing...');
    setTimeout(initPearlColorVisuals, 100);
  });
  
  document.addEventListener('shopify:block:select', function() {
    console.log('[Pearl Visual] Block selected, re-initializing...');
    setTimeout(initPearlColorVisuals, 100);
  });
}

// Export for manual initialization if needed
window.PearlColorVisual = PearlColorVisual;
window.initPearlColorVisuals = initPearlColorVisuals;
